import { useState } from 'react';

import { useInView } from 'react-intersection-observer';

import { useAppSelector } from 'dux/app/hooks';

import { Box, theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';

import { Press } from 'Apps/Main/Scenes/HomeV2/Press';

import { IngredientsSection } from 'Components/IngredientsSlider/IngredientsSection';
import Spacer from 'Components/Spacer';
import { StickyConsultationCTA } from 'Components/StickyConsultationCTA';

import { useConsultationEntryModuleAbTest } from 'abTesting/consultationEntryModule/hook';

import { productsCategories } from 'constants/products';

import { ConsultationEntryModule } from 'Blocks/ConsultationEntryModule';
import { ScienceTeam } from 'Blocks/ScienceTeam';
import { Ticker } from 'Blocks/Ticker';

import ScrollTracker from 'Containers/ScrollTracker';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import {
  clinicallyTestedContent,
  haircareProductsGridContent,
  howItWorksContent,
  scienceTeamContent,
  seeStoryContent,
  skincareProductsGridContent,
  tickerContent,
} from 'assets/content/homeV2';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { getIsAuthenticated } from 'dux/auth/selectors';
import { VARIANT1 } from 'dux/featureFlags/constants/flagVariants';

import { ClinicallyTested } from './HomeV2/ClinicallyTested';
import { FiveStarProductReviews } from './HomeV2/FiveStarProductReviews';
import { HomeHero } from './HomeV2/HomeHero';
import { HowItWorks } from './HomeV2/HowItWorks/HowItWorks';
import { InfoModal } from './HomeV2/InfoModal';
import { ProductsGrid } from './HomeV2/ProductGrid/ProductsGrid';
import type { ModalStateKey } from './HomeV2/ProductGrid/type';
import { ProsePromise } from './HomeV2/ProsePromise';
import { SeeTheStory } from './HomeV2/SeeTheStory';
import { YoutubeVideoModal } from './YoutubeVideoModal';

const PaddedBox = styled(Box)`
  padding: 0 ${theme.spacing['4x']};
  width: 100%;
  ${mediaMinWidth('md')} {
    padding: 0 ${theme.spacing['8x']};
  }
`;

const CreamBackgroundContainer = styled.div`
  width: 100%;
  background-color: ${theme.colors.neutral[200]};
  ${legacyTheme.breakpoints.up('sm')} {
    background-color: transparent;
  }
`;

const IngredientSectionContainer = styled.div`
  width: 100%;
  background-color: ${theme.colors.neutral[200]};
  ${legacyTheme.breakpoints.up('md')} {
    background-color: transparent;
  }
`;
const IngredientsSliderContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${legacyTheme.breakpoints.up('md')} {
    margin: 0 ${theme.spacing['8x']};
    padding: ${theme.spacing['4x']};
    background-color: ${theme.colors.neutral[200]};
    border-radius: ${theme.radius.xs};
  }
`;

const Home = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.7, initialInView: true });
  const [modalType, setModalType] = useState<ModalStateKey | null>(null);
  const isAuthenticated = useAppSelector(getIsAuthenticated);

  const closeModal = () => setModalType(null);

  const { isMobile } = useResponsiveVariant();

  const { variantName } = useConsultationEntryModuleAbTest({ isTrackingOnMount: true });

  return (
    <>
      <InfoModal
        isOpen={modalType === 'modalClinical'}
        onClose={closeModal}
        tooltipContent={clinicallyTestedContent.tooltipContent}
      />
      <InfoModal
        isOpen={modalType === 'modalHaircare'}
        onClose={closeModal}
        tooltipContent={haircareProductsGridContent.infoBlock.tooltip}
      />
      <InfoModal
        isOpen={modalType === 'modalSkincare'}
        onClose={closeModal}
        tooltipContent={skincareProductsGridContent.infoBlock.tooltip}
      />

      <YoutubeVideoModal
        isVideoOpen={modalType === 'modalSeeStory'}
        name="see-story"
        onClose={closeModal}
        videoId={seeStoryContent.videoId}
      />
      <YoutubeVideoModal
        isVideoOpen={modalType === 'modalHowItWorks'}
        name="how-it-works"
        onClose={closeModal}
        videoId={howItWorksContent.videoId}
      />

      <BaseScene
        footer
        FooterProps={{ hideCTA: true, shouldDisplaySustainabilityComponent: true }}
        header
        justifyContent="start"
        relativePosition
      >
        <ScrollTracker>
          <Spacer size={isMobile ? 18 : 32} />
          <PaddedBox>
            <HomeHero heroRef={heroRef} />
          </PaddedBox>
          {!isAuthenticated && variantName === VARIANT1 && <ConsultationEntryModule />}

          {!isMobile && <Spacer size={30} />}

          <Ticker
            color={theme.colors.primary[400]}
            content={tickerContent}
            dataTestId="home-revamp-ticker"
            fontSize={theme.typography.fontSize.xl}
            fontWeight={theme.typography.fontWeight.light}
            textTransform="lowercase"
            variant="p1"
          />
          <Spacer size={30} />

          <HowItWorks onOpenVideo={() => setModalType('modalHowItWorks')} />

          <Spacer size={32} />

          <PaddedBox>
            <ClinicallyTested setIsOpen={() => setModalType('modalClinical')} />
          </PaddedBox>
          <Spacer size={32} />
          <ProductsGrid
            category={productsCategories.HAIRCARE}
            dataTestId="home-revamp-products-hair"
            setIsOpen={() => setModalType('modalHaircare')}
          />
          <Spacer size={32} />
          <CreamBackgroundContainer>
            <ProductsGrid
              category={productsCategories.SKINCARE}
              dataTestId="home-revamp-products-skincare"
              setIsOpen={() => setModalType('modalSkincare')}
            />

            <Spacer size={32} />

            <PaddedBox>
              <FiveStarProductReviews />
            </PaddedBox>

            <Spacer size={32} />

            <PaddedBox>
              <ProsePromise />
            </PaddedBox>

            <Spacer size={32} />

            <IngredientSectionContainer data-testid="home-revamp-ingredients">
              <IngredientsSliderContainer>
                <IngredientsSection />
              </IngredientsSliderContainer>
            </IngredientSectionContainer>

            <Spacer size={32} />

            <PaddedBox>
              <ScienceTeam
                description={scienceTeamContent.description}
                title={scienceTeamContent.title}
              />
            </PaddedBox>

            <Spacer size={32} />

            <PaddedBox>
              <SeeTheStory onOpenVideo={() => setModalType('modalSeeStory')} />
            </PaddedBox>
            <Spacer size={32} />

            <Press />

            <Spacer size={32} />
          </CreamBackgroundContainer>
          <StickyConsultationCTA dataFrom="homepage-sticky-CTA" shouldShow={!heroInView} />
        </ScrollTracker>
      </BaseScene>
    </>
  );
};

export default Home;
